import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import GroupedItemRow from "./GroupedItemRow";
import type { GroupedManagedItem } from "@utils/grouped-bar-utils";

interface GroupedManagedListProps {
  title: string;
  items: GroupedManagedItem[];
  isDisabled: boolean;
  helperText?: string;
  onToggleVisibility: (id: string) => void;
  onReorder: (orderedIds: string[]) => void;
  onLabelChange: (id: string, label: string) => void;
  onResetLabel: (id: string) => void;
}

/**
 * A titled "Groups" or "Series" section listing the selected items for one
 * dimension with visibility toggle, inline label override and drag-and-drop
 * custom order.
 */
const GroupedManagedList: React.FC<GroupedManagedListProps> = ({
  title,
  items,
  isDisabled,
  helperText,
  onToggleVisibility,
  onReorder,
  onLabelChange,
  onResetLabel,
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const isDragDisabled = isDisabled || items.length < 2;

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = items.map((i) => i.id);
    const oldIndex = ids.indexOf(active.id as string);
    const newIndex = ids.indexOf(over.id as string);
    if (oldIndex === -1 || newIndex === -1) return;
    onReorder(arrayMove(ids, oldIndex, newIndex));
  };

  const startEdit = (item: GroupedManagedItem) => {
    setEditingId(item.id);
    setEditValue(item.displayLabel);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue("");
  };

  const saveEdit = () => {
    if (editingId) onLabelChange(editingId, editValue);
    cancelEdit();
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
        {title}
      </Typography>
      {helperText && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
          {helperText}
        </Typography>
      )}

      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          No items selected.
        </Typography>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={items.map((i) => i.id)}
            strategy={verticalListSortingStrategy}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {items.map((item) => (
                <GroupedItemRow
                  key={item.id}
                  item={item}
                  isEditing={editingId === item.id}
                  editValue={editValue}
                  isDisabled={isDisabled}
                  isDragDisabled={isDragDisabled}
                  onToggleVisibility={onToggleVisibility}
                  onStartEdit={startEdit}
                  onSaveEdit={saveEdit}
                  onCancelEdit={cancelEdit}
                  onResetLabel={onResetLabel}
                  onEditValueChange={setEditValue}
                />
              ))}
            </Box>
          </SortableContext>
        </DndContext>
      )}
    </Box>
  );
};

export default GroupedManagedList;
